import * as React from "react";
import { useQuery } from "react-query";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TablePagination, Stack, Chip } from "@mui/material";
import { ClientContactReportData, ReportFileInfo, MetricsService } from "gen/clients";
import { formatDate } from "utils/dateUtils";

export interface ClientContactsReportFilters {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  branch?: string;
  xtrfStatus?: string;
  cognitoStatus?: string;
  orgAdminStatus?: string;
  cognitoXtrfConflicts?: string;
}

interface Props {
  reportId: string;
  jsonFile: ReportFileInfo;
  filters?: ClientContactsReportFilters;
}

const ClientContactsReportTable: React.FC<Props> = ({ reportId, jsonFile, filters }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const {
    data: reportData,
    isLoading,
    error
  } = useQuery<ClientContactReportData[]>(["clientContactsReportData", reportId, jsonFile.s3Key], {
    queryFn: async () => {
      if (!jsonFile.s3Key) {
        throw new Error("JSON file s3Key is missing");
      }

      // Get secure download URL
      const downloadResponse = await MetricsService.downloadReportJobFile({
        reportId,
        s3Key: encodeURIComponent(jsonFile.s3Key)
      });

      // Fetch the actual data
      const response = await fetch(downloadResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!jsonFile.s3Key,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes
  });

  // Filter the data based on the provided filters
  const filteredData = React.useMemo(() => {
    if (!reportData || !filters) {
      return reportData || [];
    }

    return reportData.filter((row: ClientContactReportData) => {
      // Text-based filters (case-insensitive)
      if (filters.firstName && !row.firstName?.toLowerCase().includes(filters.firstName.toLowerCase())) {
        return false;
      }
      if (filters.lastName && !row.lastName?.toLowerCase().includes(filters.lastName.toLowerCase())) {
        return false;
      }
      if (filters.email && !row.email?.toLowerCase().includes(filters.email.toLowerCase())) {
        return false;
      }
      if (filters.company && !row.company?.toLowerCase().includes(filters.company.toLowerCase())) {
        return false;
      }
      if (filters.branch && !row.branch?.toLowerCase().includes(filters.branch.toLowerCase())) {
        return false;
      }

      // Dropdown filters (exact match)
      if (filters.xtrfStatus && filters.xtrfStatus !== "ALL" && row.xtrfStatus !== filters.xtrfStatus) {
        return false;
      }
      if (filters.cognitoStatus && filters.cognitoStatus !== "ALL" && row.cognitoStatus !== filters.cognitoStatus) {
        return false;
      }
      if (filters.orgAdminStatus && filters.orgAdminStatus !== "ALL") {
        const isOrgAdmin = filters.orgAdminStatus === "YES";
        if (row.orgAdminStatus !== isOrgAdmin) {
          return false;
        }
      }
      if (filters.cognitoXtrfConflicts && filters.cognitoXtrfConflicts !== "ALL") {
        const hasConflicts = filters.cognitoXtrfConflicts === "YES";
        if (row.cognitoXtrfConflicts !== hasConflicts) {
          return false;
        }
      }

      return true;
    });
  }, [reportData, filters]);

  const pageData = React.useMemo(() => {
    if (!filteredData) {
      return [];
    }
    return filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  const getOrgAdminChip = React.useCallback((isOrgAdmin: boolean) => {
    return isOrgAdmin ? (
      <Chip label="Yes" color="success" size="small" />
    ) : (
      <Chip label="No" color="default" size="small" />
    );
  }, []);

  const getCognitoStatusChip = React.useCallback((status?: string) => {
    if (!status) {
      return "-";
    }

    switch (status.toLowerCase()) {
      case "confirmed":
        return <Chip label={status} color="success" size="small" />;
      case "unconfirmed":
        return <Chip label={status} color="warning" size="small" />;
      case "force_change_password":
        return <Chip label="Force Change Password" color="info" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  }, []);

  const getCognitoXtrfConflictsChip = React.useCallback((hasConflicts: boolean) => {
    return hasConflicts ? (
      <Chip label="Yes" color="error" size="small" />
    ) : (
      <Chip label="No" color="success" size="small" />
    );
  }, []);

  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "firstName",
            title: "First Name",
            cell: (row: ClientContactReportData) => row.firstName
          },
          {
            id: "lastName",
            title: "Last Name",
            cell: (row: ClientContactReportData) => row.lastName
          },
          {
            id: "email",
            title: "Email",
            cell: (row: ClientContactReportData) => row.email
          },
          {
            id: "company",
            title: "Company",
            cell: (row: ClientContactReportData) => row.company
          },
          {
            id: "branch",
            title: "Branch",
            cell: (row: ClientContactReportData) => row.branch
          },

          {
            id: "xtrfId",
            title: "XTRF Contact ID",
            cell: (row: ClientContactReportData) => row.xtrfId
          },
          {
            id: "xtrfStatus",
            title: "Active in XTRF",
            cell: (row: ClientContactReportData) => row.xtrfStatus
          },
          {
            id: "orgAdminStatus",
            title: "Org Admin",
            cell: (row: ClientContactReportData) => getOrgAdminChip(row.orgAdminStatus)
          },
          {
            id: "cognitoStatus",
            title: "Cognito Status",
            cell: (row: ClientContactReportData) => getCognitoStatusChip(row.cognitoStatus)
          },
          {
            id: "cognitoXtrfConflicts",
            title: "Cognito/XTRF Conflicts",
            cell: (row: ClientContactReportData) => getCognitoXtrfConflictsChip(row.cognitoXtrfConflicts)
          },
          {
            id: "lastVisit",
            title: "Last Visit",
            cell: (row: ClientContactReportData) => (row.lastVisit ? formatDate(row.lastVisit) : "-")
          }
        ]}
        data={pageData}
        rowKey={(row: ClientContactReportData) => `${row.email}-${row.xtrfId}`}
        dense={true}
      />
      <TablePagination
        count={filteredData?.length || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={e => setRowsPerPage(+e.target.value)}
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Stack>
  );
};

export { ClientContactsReportTable };
